import React, { useState, useEffect } from 'react';
import { getTables, updateTableDescription } from '../services/api';

export default function TableManager() {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedTable, setSelectedTable] = useState(null);
    const [editingTable, setEditingTable] = useState(null);
    const [editDescription, setEditDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState({ total: 0, documented: 0 });

    useEffect(() => {
        loadTables();
    }, []);

    const loadTables = async () => {
        try {
            setLoading(true);
            const res = await getTables();
            setTables(res.data.tables || []);
            setStats({
                total: res.data.total || 0,
                documented: res.data.documented || 0
            });
        } catch (err) {
            console.error('Error loading tables:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredTables = tables.filter(table => 
        table.name.toLowerCase().includes(search.toLowerCase())
    );

    const handleEdit = (table) => {
        setEditingTable(table.name);
        setEditDescription(table.description);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateTableDescription(editingTable, editDescription);
            await loadTables();
            setEditingTable(null);
            alert('Description updated successfully!');
        } catch (err) {
            console.error('Error saving:', err);
            alert('Failed to save description');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4">Loading tables...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-semibold text-gray-900">Database Tables</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Total: {stats.total} tables | Documented: {stats.documented} ({Math.round(stats.documented/stats.total*100)}%)
                    </p>
                </div>
            </div>

            {/* Search */}
            <div className="mt-4">
                <input
                    type="text"
                    placeholder="Search tables..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
            </div>

            {/* Table */}
            <div className="mt-8 flex flex-col">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Table Name
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Columns
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Description
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {filteredTables.map((table) => (
                                        <tr key={table.name}>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                                                {table.name}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {table.column_count} columns
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                                {editingTable === table.name ? (
                                                    <div className="flex gap-2">
                                                        <textarea
                                                            value={editDescription}
                                                            onChange={(e) => setEditDescription(e.target.value)}
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                            rows="2"
                                                        />
                                                        <div className="flex flex-col gap-1">
                                                            <button
                                                                onClick={handleSave}
                                                                disabled={saving}
                                                                className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingTable(null)}
                                                                className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="max-w-lg truncate">
                                                        {table.description || 
                                                        <span className="text-gray-400 italic">No description</span>}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                <button
                                                    onClick={() => setSelectedTable(table)}
                                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                                >
                                                    View
                                                </button>
                                                {editingTable !== table.name && (
                                                    <button
                                                        onClick={() => handleEdit(table)}
                                                        className="text-green-600 hover:text-green-900"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Details Modal */}
            {selectedTable && (
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                            <div>
                                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                                    {selectedTable.name}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4">{selectedTable.description}</p>
                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Columns:</h4>
                                    <div className="max-h-96 overflow-y-auto">
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead>
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nullable</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Keys</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {selectedTable.columns.map((col, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-3 py-2 text-sm text-gray-900">{col.name}</td>
                                                        <td className="px-3 py-2 text-sm text-gray-500">{col.type}</td>
                                                        <td className="px-3 py-2 text-sm text-gray-500">{col.nullable ? 'Yes' : 'No'}</td>
                                                        <td className="px-3 py-2 text-sm text-gray-500">
                                                            {col.primary_key && <span className="text-blue-600">PK</span>}
                                                            {col.foreign_keys.length > 0 && 
                                                                <span className="text-green-600 ml-2">FK</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-6">
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                                    onClick={() => setSelectedTable(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}